import { useTx } from "@/i18n/Tx";
import { createContext, useContext } from "react";

// Current toolbar search text, so deep card components can highlight matches without prop drilling.
export const SearchHiContext = createContext("");

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Wraps every case-insensitive match of the active search in <mark>.
export function Hi({ text, q, className = "" }) {
  const ctx = useContext(SearchHiContext);
  const query = (q ?? ctx ?? "").trim();
  const str = useTx(text == null ? "" : String(text)) || "";
  if (!query || !str) return <span className={className}>{str}</span>;
  const parts = str.split(new RegExp(`(${esc(query)})`, "ig"));
  return (
    <span className={className}>
      {parts.map((p, i) => (p.toLowerCase() === query.toLowerCase()
        ? <mark key={i} className="rounded-sm bg-amber-200 px-0.5 text-slate-900 dark:bg-amber-400/70">{p}</mark>
        : <span key={i}>{p}</span>))}
    </span>
  );
}

// Text of every note-like entry (strings or { text }) joined for searching.
export const notesText = (arr) => (Array.isArray(arr) ? arr : []).map((n) => (typeof n === "string" ? n : n?.text || "")).join(" ");

// Every human-readable value in an object (nested arrays/objects too), lower-cased — ids, timestamps, urls and coordinates skipped.
const SKIP_KEY = /(^id$|_id$|_ids$|^_|_at$|url|^lat|^lng|^lon|token|hash|color)/i;
export function deepText(v, depth = 0) {
  if (v == null || depth > 4) return "";
  if (typeof v === "string" || typeof v === "number") return String(v).toLowerCase();
  if (Array.isArray(v)) return v.map((x) => deepText(x, depth + 1)).join(" ");
  if (typeof v === "object") return Object.entries(v).filter(([k]) => !SKIP_KEY.test(k)).map(([, x]) => deepText(x, depth + 1)).join(" ");
  return "";
}
export const deepMatch = (obj, q) => { const s = (q || "").trim().toLowerCase(); return !s || deepText(obj).includes(s); };

// First hidden field (not printed on the card) that matches — shown as a one-line "why it matched" hint.
export function dispatchMatchHint(d, q) {
  const query = (q || "").trim().toLowerCase();
  if (!query) return null;
  const has = (s) => (s || "").toLowerCase().includes(query);
  if (has(d.tech_name_snapshot)) return `Tech: ${d.tech_name_snapshot}`;
  if (has(d.customer_po_number)) return `PO #: ${d.customer_po_number}`;
  const eq = (d.equipment_names || []).find(has);
  if (eq) return `Equipment: ${eq}`;
  const st = d.dispatch_status || d.status || "";
  if (has(st.replace(/_/g, " "))) return `Status: ${st.replace(/_/g, " ")}`;
  const snippet = (s) => { const t = s || ""; const i = t.toLowerCase().indexOf(query); return i < 0 ? "" : `…${t.slice(Math.max(0, i - 20), i + query.length + 25)}…`; };
  const n = snippet(notesText(d.notes)) || snippet(notesText(d.field_notes)) || snippet(d.wo_notes_text);
  if (n) return `Note: ${n}`;
  if (has(d.site_address)) return d.site_address;
  if (has(d.contact_name)) return `Contact: ${d.contact_name}`;
  return null;
}
